import React, {
  useEffect,
  useContext,
  useLayoutEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { RouterProvider } from "react-router-dom";

// 1. Importujte potřebné věci z React Query
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import html2canvas from "html2canvas";

import { AppContext } from "./context/AppContext";
import router from "./routes";
import EditCharsModal from "./components/EditCharsModal";

// Znaky, které během editace zvýrazníme (jinak neviditelné mezery).
// key = třída pro podbarvení, chars = které kódové body obalit.
const INVISIBLE_HIGHLIGHT = [
  { char: " ", bg: "#fde68a", title: "nbsp" }, // nezlomitelná mezera
];
const PQ_MARK_ATTR = "data-pq-edit-mark";
// Atribut našeho vnitřního wrapperu pro editaci interaktivních prvků.
const PQ_WRAP_ATTR = "data-pq-edit-wrap";
// Prvky, které prohlížeč nedovolí editovat přes contentEditable na nich samých
// (interaktivní elementy). U nich editujeme obalený vnitřní text místo nich.
const NON_EDITABLE_TAGS = new Set([
  "BUTTON",
  "A",
  "LABEL",
  "SUMMARY",
  "OPTION",
]);

// 2. Vytvořte instanci QueryClient mimo komponentu App
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Příklad: Nastavení globálního staleTime na 5 minut
      // To znamená, že data budou považována za "čerstvá" 5 minut
      // a nebude se pro ně znovu odesílat request, pokud nejsou explicitně invalidována.
      staleTime: 5 * (60 * 1000),
      // Můžete přidat další globální nastavení zde
      // např. refetchOnWindowFocus: false,
    },
  },
});

const App = () => {
  const contextValue = useContext(AppContext);

  // --- Váš stávající kód (useEffect, useLayoutEffect, atd.) zůstává beze změny ---
  useEffect(() => {
    if (localStorage.getItem("userData") !== null) {
      localStorage.removeItem("userData");
    }
  }, []);

  const { setIsLogged, userData, isUserDataLoading, setIsUserDataLoading } =
    contextValue;

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if (!window.location.pathname.includes("login")) {
      setIsUserDataLoading(true);
    }
  }, []);

  if (sessionStorage.getItem("isLogged")) {
    setIsLogged(true);
  }

  // --- Editační režim textů (jen pro interní účet) ---
  const [isEditing, setIsEditing] = useState(false);
  // Stav editace držíme v refu, ať listenery vždy vidí aktuální hodnoty.
  const editRef = useRef({
    el: null, // editovaný prvek
    original: "", // původní HTML pro storno
    savedRange: null, // pozice kurzoru (přežije klik do modalu)
  });

  const isEditAccount = userData.email === "info@permoniq.com";

  // Obalí neviditelné znaky <span>em s podbarvením – jen vizuálně, během editace.
  const highlightInvisible = useCallback((el) => {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    textNodes.forEach((node) => {
      const text = node.nodeValue;
      if (!INVISIBLE_HIGHLIGHT.some((h) => text.includes(h.char))) return;
      const frag = document.createDocumentFragment();
      for (const ch of text) {
        const hit = INVISIBLE_HIGHLIGHT.find((h) => h.char === ch);
        if (hit) {
          const span = document.createElement("span");
          span.setAttribute(PQ_MARK_ATTR, "1");
          span.style.backgroundColor = hit.bg;
          span.style.borderRadius = "2px";
          span.title = hit.title;
          span.textContent = ch;
          frag.appendChild(span);
        } else {
          frag.appendChild(document.createTextNode(ch));
        }
      }
      node.parentNode.replaceChild(frag, node);
    });
  }, []);

  // Odstraní zvýrazňovací <span>y a vrátí čistý text (po skončení editace).
  const stripHighlight = useCallback((el) => {
    el.querySelectorAll(`span[${PQ_MARK_ATTR}]`).forEach((span) => {
      span.replaceWith(document.createTextNode(span.textContent));
    });
    el.normalize();
  }, []);

  // Interaktivní prvky (<button>, <a>, …) ignorují contentEditable na sobě –
  // prohlížeč do nich nepustí kurzor. Pro ně proto editujeme vnitřní <span>,
  // do kterého obalíme jejich textový obsah. Vrací element, který se má editovat.
  const resolveEditableTarget = useCallback((clicked) => {
    // Vystoupáme na nejbližší interaktivní prvek (klik mohl trefit vnořený uzel).
    let host = clicked;
    while (host && host !== document.body && !NON_EDITABLE_TAGS.has(host.tagName)) {
      host = host.parentElement;
    }
    // Klik mimo interaktivní prvek → edituj přímo to, na co se kliklo.
    if (!host || host === document.body) return clicked;

    // Pokud už wrapper existuje (opětovná editace), použij ho.
    const existing = host.querySelector(`span[${PQ_WRAP_ATTR}]`);
    if (existing) return existing;

    // Obalíme veškerý obsah hostitele do editovatelného spanu.
    const wrap = document.createElement("span");
    wrap.setAttribute(PQ_WRAP_ATTR, "1");
    while (host.firstChild) wrap.appendChild(host.firstChild);
    host.appendChild(wrap);
    return wrap;
  }, []);

  // Po skončení editace rozbalí náš pomocný wrapper zpět do hostitele.
  const unwrapEditable = useCallback((el) => {
    if (!el || el.getAttribute(PQ_WRAP_ATTR) === null) return;
    const host = el.parentNode;
    while (el.firstChild) host.insertBefore(el.firstChild, el);
    host.removeChild(el);
    host.normalize();
  }, []);

  const stopEditing = useCallback(
    (cancel) => {
      const state = editRef.current;
      const el = state.el;
      if (!el) return;
      if (cancel) {
        el.innerHTML = state.original;
      } else {
        stripHighlight(el);
      }
      el.contentEditable = "false";
      el.style.outline = el.dataset.prevOutline || "";
      el.style.boxShadow = el.dataset.prevBoxShadow || "";
      delete el.dataset.prevOutline;
      delete el.dataset.prevBoxShadow;
      el.blur();
      // Pokud jsme editovali přes vnitřní wrapper, rozbalíme ho zpět.
      unwrapEditable(el);
      editRef.current = { el: null, original: "", savedRange: null };
      setIsEditing(false);
    },
    [stripHighlight, unwrapEditable]
  );

  // Spočítá znakový offset kurzoru v rámci prvku (přežije přestavbu DOM).
  const getCaretOffset = useCallback((el, range) => {
    const pre = range.cloneRange();
    pre.selectNodeContents(el);
    pre.setEnd(range.endContainer, range.endOffset);
    return pre.toString().length;
  }, []);

  // Nastaví kurzor na daný znakový offset (po přestavbě highlight spanů).
  const setCaretOffset = useCallback((el, offset) => {
    const sel = window.getSelection();
    const range = document.createRange();
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    let remaining = offset;
    let node = walker.nextNode();
    let placed = false;
    while (node) {
      const len = node.nodeValue.length;
      if (remaining <= len) {
        range.setStart(node, remaining);
        placed = true;
        break;
      }
      remaining -= len;
      node = walker.nextNode();
    }
    if (!placed) {
      // Offset za koncem – kurzor na konec prvku.
      range.selectNodeContents(el);
      range.collapse(false);
    } else {
      range.collapse(true);
    }
    sel.removeAllRanges();
    sel.addRange(range);
    return range.cloneRange();
  }, []);

  // Vloží znak na uloženou pozici kurzoru (volá modal přes onInsert).
  const insertChar = useCallback(
    (char) => {
      const state = editRef.current;
      if (!state.el) return;
      state.el.focus();
      const sel = window.getSelection();
      // Obnovíme kurzor tam, kde byl před kliknutím do modalu.
      if (state.savedRange) {
        sel.removeAllRanges();
        sel.addRange(state.savedRange);
      }
      const range = sel.rangeCount ? sel.getRangeAt(0) : null;
      let caretOffset;
      if (!range) {
        // Bez výběru – přidáme na konec.
        state.el.append(char);
        caretOffset = state.el.textContent.length;
      } else {
        range.deleteContents();
        const node = document.createTextNode(char);
        range.insertNode(node);
        range.setStartAfter(node);
        range.collapse(true);
        // Offset si zapamatujeme jako počet znaků – přežije přestavbu níže.
        caretOffset = getCaretOffset(state.el, range);
      }
      // Překreslíme zvýraznění (mohli jsme vložit nbsp); rozbije to DOM nody...
      stripHighlight(state.el);
      highlightInvisible(state.el);
      // ...proto kurzor obnovíme podle znakového offsetu a uložíme novou pozici.
      state.savedRange = setCaretOffset(state.el, caretOffset);
    },
    [highlightInvisible, stripHighlight, getCaretOffset, setCaretOffset]
  );

  // Vloží SVG ikonu (ze spritu /img/icons.svg) na uloženou pozici kurzoru.
  const insertIcon = useCallback(
    (iconId) => {
      const state = editRef.current;
      if (!state.el) return;
      state.el.focus();
      const sel = window.getSelection();
      // Obnovíme kurzor tam, kde byl před kliknutím do modalu.
      if (state.savedRange) {
        sel.removeAllRanges();
        sel.addRange(state.savedRange);
      }
      // Sestavíme stejnou strukturu, jakou používají ostatní komponenty:
      // <span class="icon"><svg class="icon__svg"><use href="…#id"/></svg></span>
      const SVG_NS = "http://www.w3.org/2000/svg";
      const wrapper = document.createElement("span");
      // Modifikátor icon--<id> dává wrapperu rozměry (šířku + aspect-ratio
      // přes :before); bez něj je .icon 0×0 a absolutně pozicované SVG se
      // nevykreslí.
      wrapper.className = `icon icon--${iconId}`;
      const svg = document.createElementNS(SVG_NS, "svg");
      svg.setAttribute("class", "icon__svg");
      const use = document.createElementNS(SVG_NS, "use");
      use.setAttributeNS(
        "http://www.w3.org/1999/xlink",
        "xlink:href",
        `/img/icons.svg#${iconId}`
      );
      use.setAttribute("href", `/img/icons.svg#${iconId}`);
      svg.appendChild(use);
      wrapper.appendChild(svg);

      const range = sel.rangeCount ? sel.getRangeAt(0) : null;
      const inEl = range && state.el.contains(range.commonAncestorContainer);
      if (!range || !inEl) {
        // Bez (platného) výběru uvnitř prvku – přidáme na konec.
        state.el.appendChild(wrapper);
      } else {
        range.deleteContents();
        range.insertNode(wrapper);
        range.setStartAfter(wrapper);
        range.collapse(true);
      }
      // Zapamatujeme si novou pozici kurzoru (za vloženou ikonou).
      const after = document.createRange();
      after.setStartAfter(wrapper);
      after.collapse(true);
      sel.removeAllRanges();
      sel.addRange(after);
      state.savedRange = after.cloneRange();
    },
    []
  );

  // Zkopíruje aktuální (upravený) text editovaného prvku do schránky.
  const copyText = useCallback(async () => {
    const el = editRef.current.el;
    if (!el) return;
    // Pracujeme na klonu, ať nezasáhneme zobrazení; odstraníme highlight spany.
    const clone = el.cloneNode(true);
    clone.querySelectorAll(`span[${PQ_MARK_ATTR}]`).forEach((span) => {
      span.replaceWith(document.createTextNode(span.textContent));
    });
    const text = clone.innerText;
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error("Kopírování textu selhalo:", err);
    }
  }, []);

  // Vyrenderuje aktuálně viditelný výřez stránky (viewport) do canvasu.
  const renderSnapshot = useCallback(async () => {
    const el = editRef.current.el;

    // Dočasně schováme editorské artefakty (outline, box-shadow, podbarvení
    // nbsp), ať se na snímku neobjeví.
    let prevOutline, prevBoxShadow, marks, markBgs;
    if (el) {
      prevOutline = el.style.outline;
      prevBoxShadow = el.style.boxShadow;
      el.style.outline = "none";
      el.style.boxShadow = "none";
      marks = el.querySelectorAll(`span[${PQ_MARK_ATTR}]`);
      markBgs = [];
      marks.forEach((m) => {
        markBgs.push(m.style.backgroundColor);
        m.style.backgroundColor = "transparent";
      });
    }

    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;

    try {
      const canvas = await html2canvas(document.body, {
        // Vyřízneme jen oblast aktuálního viewportu.
        x: scrollX,
        y: scrollY,
        width: vw,
        height: vh,
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: document.documentElement.scrollHeight,
        scrollX: -scrollX,
        scrollY: -scrollY,
        scale: window.devicePixelRatio || 2,
        useCORS: true,
        logging: false,
      });
      return canvas;
    } catch (err) {
      console.error("Snímek se nepodařilo vytvořit:", err);
      return null;
    } finally {
      // Vrátíme editorské vizuály zpět.
      if (el) {
        el.style.outline = prevOutline;
        el.style.boxShadow = prevBoxShadow;
        marks.forEach((m, i) => {
          m.style.backgroundColor = markBgs[i];
        });
      }
    }
  }, []);

  // Snímek prvku → do schránky jako obrázek (PNG).
  const snapshotToClipboard = useCallback(async () => {
    const canvas = await renderSnapshot();
    if (!canvas) return;
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      try {
        await navigator.clipboard.write([
          new window.ClipboardItem({ "image/png": blob }),
        ]);
      } catch (err) {
        console.error("Uložení snímku do schránky selhalo:", err);
      }
    }, "image/png");
  }, [renderSnapshot]);

  // Snímek prvku → stáhnout lokálně jako PNG.
  const snapshotToFile = useCallback(async () => {
    const canvas = await renderSnapshot();
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `snimek-${window.location.pathname.replace(/\W+/g, "-")}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  }, [renderSnapshot]);

  useEffect(() => {
    if (!isEditAccount) return;

    const EDIT_OUTLINE = "2px dashed #dafd82";

    const startEditing = (el) => {
      el.dataset.prevOutline = el.style.outline;
      el.dataset.prevBoxShadow = el.style.boxShadow;
      el.style.outline = EDIT_OUTLINE;
      el.style.boxShadow = "0 0 0 4px rgba(218, 253, 130, 0.45)";
      el.contentEditable = "true";
      el.focus();

      editRef.current = {
        el,
        original: el.innerHTML,
        savedRange: null,
      };
      highlightInvisible(el);
      setIsEditing(true);
    };

    const handleAltClick = (event) => {
      if (!event.altKey) return;
      const state = editRef.current;
      if (!event.target) return;

      // Zabráníme navigaci/akci prvku (např. <a>, <button>).
      event.preventDefault();
      event.stopPropagation();

      // Edituji-li něco jiného, nejdřív to potvrdím...
      if (state.el) stopEditing(false);
      // U interaktivních prvků (button, odkaz) editujeme vnitřní text-wrapper,
      // protože contentEditable na nich samých prohlížeč ignoruje.
      const el = resolveEditableTarget(event.target);
      // Klik na týž prvek, který už edituji → nech ho být.
      if (el === state.el) return;
      // ...a rovnou přejdu na nově kliknutý prvek.
      startEditing(el);
    };

    const handleKeyDown = (event) => {
      if (!editRef.current.el) return;
      if (event.key === "Escape") {
        event.preventDefault();
        stopEditing(true); // storno – vrátí původní obsah
      } else if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        stopEditing(false); // potvrdí; Shift+Enter = nový řádek
      }
    };

    // Při každé změně výběru si pamatujeme pozici kurzoru v editovaném prvku.
    const handleSelectionChange = () => {
      const state = editRef.current;
      if (!state.el) return;
      const sel = window.getSelection();
      if (sel.rangeCount && state.el.contains(sel.anchorNode)) {
        state.savedRange = sel.getRangeAt(0).cloneRange();
      }
    };

    document.addEventListener("click", handleAltClick, true);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("selectionchange", handleSelectionChange);

    window.appContext = Object.freeze(contextValue);

    return () => {
      stopEditing(true);
      document.removeEventListener("click", handleAltClick, true);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, [
    isEditAccount,
    contextValue,
    stopEditing,
    highlightInvisible,
    resolveEditableTarget,
  ]);

  useEffect(() => {
    console.log(`version: ${process.env.REACT_APP_VERSION}\n`);
  }, []);
  // --- Konec vašeho stávajícího kódu ---

  // 3. Obalte RouterProvider pomocí QueryClientProvider
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      {/* 4. Volitelně přidejte React Query DevTools (zobrazí se jen v development módu) */}
      {isEditing && (
        <EditCharsModal
          onInsert={insertChar}
          onInsertIcon={insertIcon}
          onCopyText={copyText}
          onSnapshotClipboard={snapshotToClipboard}
          onSnapshotFile={snapshotToFile}
          onClose={() => stopEditing(false)}
        />
      )}
    </QueryClientProvider>
  );
};

export default App;
